import React, { useCallback, useState } from 'react'
import { useEditorState } from '@tiptap/react'
import type { Editor } from '@tiptap/react'
import {
  Button,
  TextField,
  Flex,
  DropdownMenu,
  Popover,
  Select,
  Tooltip,
} from '@radix-ui/themes'
import type { MergeTagType } from '../../extensions/merge-tag'
import { TextColorIcon, BgColorIcon, MergeTagIcon } from './icons'
import styles from './toolbar.module.css'

// -- Font Size Select (Radix Select) --

const FONT_SIZES = [
  { value: 'default', label: 'Default' },
  { value: '12px', label: '12px' },
  { value: '14px', label: '14px' },
  { value: '16px', label: '16px' },
  { value: '18px', label: '18px' },
  { value: '20px', label: '20px' },
  { value: '24px', label: '24px' },
  { value: '32px', label: '32px' },
]

export function FontSizeSelect({ editor }: { editor: Editor }) {
  const currentFontSize = useEditorState({
    editor,
    selector: ({ editor: ed }) =>
      (ed.getAttributes('textStyle').fontSize as string) || '',
  })

  return (
    <Select.Root
      size="1"
      value={currentFontSize || 'default'}
      onValueChange={val => {
        if (val === 'default') {
          editor.chain().focus().unsetFontSize().run()
        } else {
          editor.chain().focus().setFontSize(val).run()
        }
      }}
    >
      <Tooltip content="Font size">
        <Select.Trigger
          variant="ghost"
          placeholder="Font size"
          aria-label="Font size"
          className={styles.toolbarSelectTrigger}
        />
      </Tooltip>
      <Select.Content>
        {FONT_SIZES.map(size => (
          <Select.Item key={size.value} value={size.value}>
            {size.label}
          </Select.Item>
        ))}
      </Select.Content>
    </Select.Root>
  )
}

// -- Text Type Select (headings dropdown for email mode) --

const TEXT_TYPES = [
  { value: 'heading-1', label: 'Large title', level: 1 },
  { value: 'heading-2', label: 'Title 1', level: 2 },
  { value: 'heading-3', label: 'Title 2', level: 3 },
  { value: 'heading-4', label: 'Title 3', level: 4 },
  { value: 'paragraph', label: 'Regular text', level: 0 },
]

export function TextTypeSelect({ editor }: { editor: Editor }) {
  const currentValue = useEditorState({
    editor,
    selector: ({ editor: ed }) => {
      for (const type of TEXT_TYPES) {
        if (type.level > 0 && ed.isActive('heading', { level: type.level }))
          return type.value
      }
      return 'paragraph'
    },
  })

  return (
    <Select.Root
      size="1"
      value={currentValue}
      onValueChange={val => {
        const type = TEXT_TYPES.find(t => t.value === val)
        if (!type) return
        if (type.level === 0) {
          editor.chain().focus().setParagraph().run()
        } else {
          editor
            .chain()
            .focus()
            .toggleHeading({ level: type.level as 1 | 2 | 3 | 4 })
            .run()
        }
      }}
    >
      <Tooltip content="Text type">
        <Select.Trigger
          variant="ghost"
          placeholder="Text type"
          aria-label="Text type"
          className={styles.toolbarSelectTrigger}
        />
      </Tooltip>
      <Select.Content>
        {TEXT_TYPES.map(type => (
          <Select.Item key={type.value} value={type.value}>
            {type.label}
          </Select.Item>
        ))}
      </Select.Content>
    </Select.Root>
  )
}

// -- Color Picker (text color & background color) --

const COLOR_PALETTE = [
  '#000000',
  '#ff9869',
  '#ff45ca',
  '#d095ff',
  '#4e98ff',
  '#37ff3e',
  '#fffc52',
  '#910460',
  '#3d5b01',
  '#ffffff',
]

export function ColorPickerButton({
  editor,
  type,
  title,
  ariaLabel,
}: {
  editor: Editor
  type: 'text' | 'background'
  title: string
  ariaLabel: string
}) {
  const [customColor, setCustomColor] = useState('')

  const currentColor = useEditorState({
    editor,
    selector: ({ editor: ed }) =>
      type === 'text'
        ? (ed.getAttributes('textStyle').color as string) || ''
        : (ed.getAttributes('highlight').color as string) || '',
  })

  const applyColor = useCallback(
    (color: string) => {
      if (type === 'text') {
        editor.chain().focus().setColor(color).run()
      } else {
        editor.chain().focus().toggleHighlight({ color }).run()
      }
    },
    [editor, type],
  )

  const normalizeHex = (value: string): string | null => {
    const v = value.trim().replace(/^#/, '')
    if (!/^[0-9a-fA-F]{3,8}$/.test(v)) return null
    return `#${v}`
  }

  const removeColor = useCallback(() => {
    if (type === 'text') {
      editor.chain().focus().unsetColor().run()
    } else {
      editor.chain().focus().unsetHighlight().run()
    }
  }, [editor, type])

  return (
    <Popover.Root>
      <Tooltip content={title}>
        <Popover.Trigger>
          <button
            type="button"
            className={`${styles.toolbarButton} ${
              currentColor ? styles.toolbarButtonActive : ''
            }`}
            aria-label={ariaLabel}
          >
            {type === 'text' ? (
              <TextColorIcon color={currentColor} />
            ) : (
              <BgColorIcon color={currentColor} />
            )}
          </button>
        </Popover.Trigger>
      </Tooltip>
      <Popover.Content size="1" side="top">
        <div className={styles.colorGrid}>
          {COLOR_PALETTE.map(color => (
            <button
              key={color}
              type="button"
              className={`${styles.colorSwatch} ${
                currentColor === color ? styles.colorSwatchActive : ''
              }`}
              onClick={() => applyColor(color)}
              title={color}
              aria-label={`Set ${type} color to ${color}`}
            >
              {/* Dynamic background color requires inline style */}
              <span
                className={styles.colorSwatchInner}
                style={{ backgroundColor: color }} // eslint-disable-line react/forbid-dom-props
              />
            </button>
          ))}
        </div>
        <Flex gap="2" mt="2" align="center">
          <TextField.Root
            size="1"
            placeholder="#hex"
            value={customColor}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setCustomColor(e.target.value)
            }
            onKeyDown={(e: React.KeyboardEvent) => {
              if (e.key !== 'Enter') return
              const hex = normalizeHex(customColor)
              if (hex) {
                applyColor(hex)
                setCustomColor('')
              }
            }}
          />
          <Button
            size="1"
            variant="soft"
            onClick={() => {
              const hex = normalizeHex(customColor)
              if (hex) {
                applyColor(hex)
                setCustomColor('')
              }
            }}
          >
            Apply
          </Button>
          {currentColor && (
            <Button size="1" variant="soft" color="gray" onClick={removeColor}>
              Clear
            </Button>
          )}
        </Flex>
      </Popover.Content>
    </Popover.Root>
  )
}

// -- Merge Tags Dropdown (Radix DropdownMenu) --

const MERGE_TAGS: { type: MergeTagType; label: string }[] = [
  { type: 'first-name-merge-tag', label: 'First name' },
  { type: 'last-name-merge-tag', label: 'Last name' },
  { type: 'full-name-merge-tag', label: 'Full name' },
]

export function MergeTagsMenu({ editor }: { editor: Editor }) {
  return (
    <DropdownMenu.Root>
      <Tooltip content="Insert merge tag">
        <DropdownMenu.Trigger>
          <button
            type="button"
            className={styles.toolbarButton}
            aria-label="Insert merge tag"
          >
            <MergeTagIcon />
          </button>
        </DropdownMenu.Trigger>
      </Tooltip>
      <DropdownMenu.Content>
        {MERGE_TAGS.map(tag => (
          <DropdownMenu.Item
            key={tag.type}
            onSelect={() => editor.commands.insertMergeTag(tag.type)}
          >
            {tag.label}
          </DropdownMenu.Item>
        ))}
      </DropdownMenu.Content>
    </DropdownMenu.Root>
  )
}
